import { createRouter, createWebHistory } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuth } from '@/services/use-auth'
import guardSections from '@/types/guard-sections'
import { loadLanguageAsync } from '@/i18n'
import routeNames from '@/types/route-names'
import { useSettingsStore } from '@/store/settings/settings'
import permissions from '@/store/permissions'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: routeNames.LOGIN,
      component: () => import('@/views/Login/Index.vue'),
      meta: {
        auth: false,
        title: 'Sign in',
        description:
          'All ad formats that you need: push, popunder, banner, video, in-page, native, Interstitial',
        layoutName: 'DefaultNewLayout',
        localeFiles: ['login.json']
      }
    },
    {
      path: '/login-mcp',
      name: routeNames.LOGIN_MCP,
      component: () => import('@/views/LoginMcp.vue'),
      meta: {
        auth: true,
        title: 'MCP login',
        layoutName: 'DefaultLayout',
        section: guardSections.MCP
      }
    },
    {
      path: '/register',
      name: routeNames.REGISTER,
      component: () => import('@/views/Register/Index.vue'),
      meta: {
        auth: false,
        title: 'Registration page',
        description: 'Next Generation Self-Serve Ad Network Platform',
        layoutName: 'DefaultNewLayout',
        localeFiles: ['register.json']
      }
    },
    {
      path: '/logout',
      name: routeNames.LOGOUT,
      component: () => import('@/views/Logout.vue'),
      meta: {
        auth: true,
        title: 'Logout',
        layoutName: 'DefaultLayout'
      }
    },
    {
      path: '/unsubscribe/:email',
      name: routeNames.UNSUBSCRIBE,
      component: () => import('@/views/UnsubscribePage.vue'),
      meta: {
        title: 'Unsubscribe',
        layoutName: 'DefaultNewLayout',
        localeFiles: ['unsubscribe.json'],
        hideCabinetSwitcher: true
      }
    },
    {
      path: '/register/:userId/confirm/:code',
      name: routeNames.CONFIRM_EMAIL,
      component: () => import('@/views/ConfirmEmailPage.vue'),
      meta: {
        title: 'ConfirmEmail',
        layoutName: 'DefaultNewLayout',
        localeFiles: ['register.json'],
        hideCabinetSwitcher: true
      }
    },
    {
      path: '/impersonate',
      name: routeNames.IMPERSONATE,
      component: () => import('@/views/Impersonate.vue'),
      meta: {
        layoutName: 'DefaultLayout'
      }
    },
    {
      path: '/password/reset',
      name: routeNames.RESET_PASSWORD,
      component: () => import('@/views/ResetPassword.vue'),
      meta: {
        title: 'Reset password',
        layoutName: 'DefaultNewLayout',
        localeFiles: ['login.json'],
        hideCabinetSwitcher: true
      }
    },
    {
      path: '/password/forgot',
      name: routeNames.FORGOT_PASSWORD,
      component: () => import('@/views/ForgotPassword.vue'),
      meta: {
        auth: false,
        title: 'Forgot password',
        layoutName: 'DefaultNewLayout',
        localeFiles: ['login.json'],
        hideCabinetSwitcher: true
      }
    },
    {
      path: '',
      name: routeNames.MAIN,
      component: () => import('@/views/Main.vue'),
      meta: {
        auth: true,
        layoutName: 'MainLayouts'
      }
    },
    {
      path: '/dashboard',
      name: routeNames.DASHBOARD,
      component: () => import('@/views/Dashboard/Index.vue'),
      meta: {
        auth: true,
        title: 'Dashboard',
        layoutName: 'MainLayouts',
        section: guardSections.DASHBOARD,
        localeFiles: ['dashboard.json']
      }
    },
    {
      path: '/notification',
      name: routeNames.NOTIFICATION,
      component: () => import('@/views/Notification/Index.vue'),
      meta: {
        auth: true,
        title: 'Notification',
        layoutName: 'MainLayouts',
        section: guardSections.NOTIFICATIONS,
        localeFiles: ['notification_bell.json']
      }
    },
    {
      path: '/personal',
      name: routeNames.PERSONAL,
      component: () => import('@/views/Personal/Index.vue'),
      meta: {
        auth: true,
        title: 'Personal',
        layoutName: 'MainLayouts',
        section: guardSections.PROFILE,
        localeFiles: ['personal.json']
      }
    },
    {
      path: '/verifications/:type',
      name: routeNames.VERIFICATIONS,
      component: () => import('@/views/Verifications/Index.vue'),
      meta: {
        auth: true,
        title: 'Verification request',
        layoutName: 'MainLayouts',
        localeFiles: ['verifications.json']
      }
    },
    {
      path: '/api',
      name: routeNames.API,
      component: () => import('@/views/Api/Index.vue'),
      meta: {
        auth: true,
        title: 'API',
        layoutName: 'MainLayouts',
        section: guardSections.API,
        localeFiles: ['api.json']
      }
    },
    {
      path: '/tracking',
      name: routeNames.TRACKING,
      component: () => import('@/views/Tracking/Index.vue'),
      meta: {
        auth: true,
        title: 'Tracking',
        layoutName: 'MainLayouts',
        section: guardSections.TRACKING,
        localeFiles: ['tracking.json']
      }
    },
    {
      path: '/ads',
      name: routeNames.ADS,
      component: () => import('@/views/Ads/Index.vue'),
      meta: {
        auth: true,
        title: 'Ads',
        layoutName: 'MainLayouts',
        section: guardSections.ADS,
        localeFiles: ['ads.json']
      }
    },
    {
      path: '/ads/create',
      name: routeNames.CREATE_AD,
      component: () => import('@/views/CreateAndEditAds/Index.vue'),
      meta: {
        auth: true,
        title: 'Create ad',
        layoutName: 'MainLayouts',
        section: guardSections.ADS
      }
    },
    {
      path: '/ads/:id',
      name: routeNames.AD,
      component: () => import('@/views/Ad/Index.vue'),
      meta: {
        auth: true,
        title: 'Ad',
        layoutName: 'MainLayouts',
        section: guardSections.ADS,
        localeFiles: ['ads.json']
      }
    },
    {
      path: '/ads/:id/duplicate',
      name: routeNames.DUPLICATE_AD,
      component: () => import('@/views/CreateAndEditAds/Index.vue'),
      meta: {
        auth: true,
        title: 'Duplicate ad',
        layoutName: 'MainLayouts',
        section: guardSections.ADS
      }
    },
    {
      path: '/ads/:id/edit',
      name: routeNames.EDIT_AD,
      component: () => import('@/views/CreateAndEditAds/Index.vue'),
      meta: {
        auth: true,
        title: 'Edit ad',
        layoutName: 'MainLayouts',
        section: guardSections.ADS
      }
    },
    {
      path: '/campaigns/create',
      name: routeNames.CREATE_CAMPAIGN,
      component: () => import('@/views/CampaignForm/Index.vue'),
      meta: {
        auth: true,
        title: 'Create campaign',
        layoutName: 'MainLayouts',
        section: guardSections.CAMPAIGNS,
        localeFiles: ['campaign_form.json']
      }
    },
    {
      path: '/campaigns/create-offer-campaign',
      name: routeNames.OFFER_CAMPAIGN,
      component: () => import('@/views/CampaignForm/Index.vue'),
      meta: {
        auth: true,
        title: 'Create offer campaign',
        layoutName: 'MainLayouts',
        section: guardSections.CAMPAIGN_OFFERS,
        localeFiles: ['campaign_form.json']
      }
    },
    {
      path: '/campaigns',
      name: routeNames.CAMPAIGNS,
      component: () => import('@/views/Campaigns/Index.vue'),
      meta: {
        auth: true,
        title: 'Campaigns',
        layoutName: 'MainLayouts',
        section: guardSections.CAMPAIGNS,
        localeFiles: ['campaign.json']
      }
    },
    {
      path: '/campaigns/:id',
      name: routeNames.CAMPAIGN,
      props: true,
      component: () => import('@/views/Campaign/Index.vue'),
      meta: {
        auth: true,
        title: 'Campaign',
        layoutName: 'MainLayouts',
        section: guardSections.CAMPAIGNS,
        localeFiles: ['campaign.json', 'ads.json']
      }
    },
    {
      path: '/campaigns/:id/edit',
      name: routeNames.EDIT_CAMPAIGN,
      props: true,
      component: () => import('@/views/CampaignForm/Index.vue'),
      meta: {
        auth: true,
        title: 'Edit campaign',
        layoutName: 'MainLayouts',
        section: guardSections.CAMPAIGNS,
        localeFiles: ['campaign_form.json']
      }
    },
    {
      path: '/campaigns/:id/duplicate',
      name: routeNames.DUPLICATE_CAMPAIGN,
      props: true,
      component: () => import('@/views/CampaignForm/Index.vue'),
      meta: {
        auth: true,
        title: 'Duplicate campaign',
        layoutName: 'MainLayouts',
        section: guardSections.CAMPAIGNS,
        localeFiles: ['campaign_form.json']
      }
    },
    {
      path: '/upsale/:campaignId',
      name: routeNames.UPSALE,
      component: () => import('@/views/Upsale/Index.vue'),
      meta: {
        auth: true,
        title: 'Upsale',
        layoutName: 'MainLayouts',
        section: guardSections.CAMPAIGN_OFFERS,
        localeFiles: ['upsale.json']
      }
    },
    {
      path: '/finances',
      name: routeNames.FINANCES,
      component: () => import('@/views/Finances/Index.vue'),
      meta: {
        auth: true,
        title: 'Finances',
        layoutName: 'MainLayouts',
        section: guardSections.FINANCES,
        localeFiles: ['finances.json']
      }
    },
    {
      path: '/rtb',
      name: routeNames.RTB,
      component: () => import('@/views/Rtb.vue'),
      meta: {
        auth: true,
        layoutName: 'MainLayouts',
        section: guardSections.RTB
      },
      children: [
        {
          path: 'endpoints',
          name: routeNames.RTB_ENDPOINTS,
          component: () => import('@/views/RtbEndpoints/Index.vue'),
          meta: {
            title: 'RTB Endpoints',
            layoutName: 'MainLayouts',
            localeFiles: ['rtb.json']
          }
        },
        {
          path: 'endpoints/create',
          name: routeNames.RTB_CREATE_ENDPOINT,
          component: () => import('@/views/RtbCreateEndpoint/Index.vue'),
          meta: {
            title: 'RTB Create endpoint',
            layoutName: 'MainLayouts',
            localeFiles: ['rtb.json']
          }
        },
        {
          path: 'endpoints/edit/:id',
          name: routeNames.RTB_EDIT_ENDPOINT,
          props: true,
          component: () => import('@/views/RtbCreateEndpoint/Index.vue'),
          meta: {
            title: 'RTB Edit endpoint',
            layoutName: 'MainLayouts',
            localeFiles: ['rtb.json']
          }
        },
        {
          path: 'network',
          name: routeNames.RTB_NETWORK,
          component: () => import('@/views/RtbNetwork.vue'),
          meta: {
            title: 'RTB Network status',
            layoutName: 'MainLayouts',
            localeFiles: ['rtb.json']
          }
        },
        {
          path: 'network/create',
          name: routeNames.RTB_CREATE_NETWORK,
          component: () => import('@/views/RtbCreateNetwork.vue'),
          meta: {
            title: 'RTB Create network',
            layoutName: 'MainLayouts',
            localeFiles: ['rtb.json']
          }
        }
      ]
    },
    {
      path: '/campaign-statistics',
      name: routeNames.CAMPAIGN_STATISTICS,
      component: () => import('@/views/CampaignsStatistics/Index.vue'),
      meta: {
        auth: true,
        title: 'Campaigns statistics',
        layoutName: 'MainLayouts',
        section: guardSections.CAMPAIGN_STATISTICS,
        localeFiles: ['campaign_statistics.json']
      }
    },
    {
      path: '/rtb-statistics',
      name: routeNames.RTB_STATISTICS,
      component: () => import('@/views/RtbStatistics/Index.vue'),
      meta: {
        auth: true,
        title: 'RTB statistics',
        layoutName: 'MainLayouts',
        section: guardSections.RTB_STATISTICS,
        localeFiles: ['rtb.json']
      }
    },
    {
      path: '/ssp-statistics',
      name: routeNames.SSP_STATISTICS,
      component: () => import('@/views/SspStatistics/Index.vue'),
      meta: {
        auth: true,
        title: 'SSP statistics',
        layoutName: 'MainLayouts',
        section: guardSections.SSP_STATISTICS,
        localeFiles: ['rtb.json']
      }
    },
    {
      path: '/traffic-chart',
      name: routeNames.TRAFFIC_CHART,
      component: () => import('@/views/TrafficChart/Index.vue'),
      meta: {
        auth: true,
        title: 'Traffic chart',
        layoutName: 'MainLayouts',
        section: guardSections.TRAFFIC_CHART,
        localeFiles: ['traffic_chart.json']
      }
    },
    {
      path: '/add-funds',
      name: routeNames.ADD_FUNDS,
      component: () => import('@/views/AddFunds/Index.vue'),
      meta: {
        auth: true,
        title: 'Add funds',
        layoutName: 'MainLayouts',
        section: guardSections.ADD_FUNDS,
        localeFiles: ['add_funds.json']
      }
    },
    {
      path: '/add-funds/success',
      name: routeNames.ADD_FUNDS_SUCCESS,
      component: () => import('@/views/AddFundsSuccess.vue'),
      meta: {
        auth: true,
        layoutName: 'MainLayouts',
        section: guardSections.ADD_FUNDS,
        localeFiles: ['add_funds.json']
      }
    },
    {
      path: '/add-funds/error',
      name: routeNames.ADD_FUNDS_ERROR,
      component: () => import('@/views/AddFundsError.vue'),
      meta: {
        auth: true,
        layoutName: 'MainLayouts',
        section: guardSections.ADD_FUNDS,
        localeFiles: ['add_funds.json']
      }
    },
    {
      path: '/registration-succeeded',
      name: routeNames.REGISTRATION_SUCCEEDED,
      component: () => import('@/views/RegistrationSucceededPage/Index.vue'),
      meta: {
        layoutName: 'EmptyLayout',
        section: guardSections.SUCCESSFUL_REGISTRATION,
        localeFiles: ['register.json']
      }
    },
    {
      path: '/providers',
      name: routeNames.PROVIDERS,
      component: () => import('@/views/Providers/Index.vue'),
      meta: {
        auth: true,
        title: 'Providers',
        layoutName: 'MainLayouts',
        section: guardSections.TRAFFIC_PROVIDERS,
        localeFiles: ['providers.json']
      }
    },
    {
      path: '/providers-subscription',
      name: routeNames.PROVIDERS_SUBSCRIPTION,
      component: () => import('@/views/Providers/ProvidersSubscription/Index.vue'),
      meta: {
        auth: true,
        title: 'Providers Promo',
        layoutName: 'MainLayouts',
        section: guardSections.TRAFFIC_PROVIDERS,
        localeFiles: ['providers.json']
      }
    },
    {
      path: '/provider/create',
      name: routeNames.CREATE_PROVIDER,
      component: () => import('@/views/CreateProvider.vue'),
      meta: {
        auth: true,
        title: 'Create providers',
        layoutName: 'MainLayouts',
        section: guardSections.TRAFFIC_PROVIDERS,
        localeFiles: ['providers.json']
      }
    },
    {
      path: '/marketplace',
      name: routeNames.MARKETPLACE,
      component: () => import('@/views/Marketplace/Index.vue'),
      meta: {
        auth: true,
        title: 'Marketplace',
        layoutName: 'MainLayouts',
        section: guardSections.FLAT_DEALS,
        permissions: [permissions.MANAGE_CAMPAIGN_SITE_TARGET],
        localeFiles: ['marketplace.json']
      }
    },
    {
      path: '/flat-deal/:id',
      name: routeNames.FLAT_DEAL,
      component: () => import('@/views/FlatDeal/Index.vue'),
      meta: {
        auth: true,
        title: 'FlatDeal',
        layoutName: 'MainLayouts',
        section: guardSections.FLAT_DEALS,
        permissions: [permissions.MANAGE_CAMPAIGN_SITE_TARGET],
        localeFiles: ['marketplace.json']
      }
    },
    {
      path: '/flat-deal/:id/offer',
      name: routeNames.FLAT_DEAL_OFFER,
      component: () => import('@/views/FlatDeal/Offer/Index.vue'),
      meta: {
        auth: true,
        title: 'Direct Deal Offer',
        layoutName: 'EmptyLayout',
        section: guardSections.FLAT_DEALS,
        permissions: [permissions.MANAGE_CAMPAIGN_SITE_TARGET],
        localeFiles: ['marketplace.json']
      }
    },
    {
      path: '/:pathMatch(.*)*',
      name: routeNames.NOT_FOUND,
      component: () => import('@/views/NotFound.vue'),
      meta: {
        title: 'Page not found',
        layoutName: 'DefaultLayout',
        localeFiles: ['page_404.json']
      }
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.path === from.path) {
      return false
    }
    return { left: 0, top: 0 }
  }
})

router.beforeEach(async (to, from) => {
  if (to.name === routeNames.IMPERSONATE) {
    return true
  }
  const auth = useAuth()
  await auth.initialize()

  const settingsStore = useSettingsStore()
  const { applicationIsAuth, applicationDataFetched, adNetwork } = storeToRefs(settingsStore)
  if (auth.isAuth === true && !applicationIsAuth.value) {
    await Promise.all([auth.fetchUser(), settingsStore.fetchSettings(auth.isAuth)])
  }

  if (auth.isAuth === false && (applicationIsAuth.value || !applicationDataFetched.value)) {
    await settingsStore.fetchSettings(auth.isAuth)
  }

  await settingsStore.setBackRouteName(from.name)
  if (auth.isAuth) {
    const nextRoutingPath = sessionStorage.getItem('next_routing_path')
    sessionStorage.removeItem('next_routing_path')

    if (nextRoutingPath && nextRoutingPath !== to.fullPath) {
      return nextRoutingPath
    }
  }
  if (![auth.isAuth, undefined].includes(to.meta.auth)) {
    if (!auth.isAuth) {
      sessionStorage.setItem('next_routing_path', to.fullPath)
    }
    return { name: to.meta.auth ? routeNames.LOGIN : routeNames.MAIN }
  }

  if (to.meta.section) {
    const foundSection = adNetwork.value.guardSections.find(({ name }) => name === to.meta.section)
    if (!foundSection) {
      return { name: routeNames.NOT_FOUND }
    }
  }

  if (to.meta.permissions?.length) {
    const userPermissions = auth.user?.permissions ?? []
    const hasAllPermissions = to.meta.permissions.every((permission) => userPermissions.includes(permission))
    if (!hasAllPermissions) {
      return { name: routeNames.NOT_FOUND }
    }
  }
  await loadLanguageAsync(localStorage.locale, to.meta.localeFiles)

  return true
})

export default router
